import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { masterEventService } from '../services/masterEvent';
import { teamsService } from '../services/teams';
import { scoringService } from '../services/scoring';
import { feedService } from '../services/feed';
import { useAuth } from './AuthContext';
import { competitionService } from '../services/competition';

const MasterEventContext = createContext(null);

// Role hierarchy:
// 'owner'          - system level: can delete/edit events from the selector screen
// 'event-admin'    - full event rights: add/remove activities, members, teams, scoring
// 'co-organizer'   - same as event-admin
// 'activity-admin' - can manage specific activities they're assigned to
// 'participant'    - regular member

export function MasterEventProvider({ masterEventId, children }) {
  const { user } = useAuth();
  const [masterEvent, setMasterEvent] = useState(null);
  const [members, setMembers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scores, setScores] = useState([]);
  const [feed, setFeed] = useState([]);
  const [myMembership, setMyMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!masterEventId) { setLoading(false); return; }
    try {
    const me = await masterEventService.getById(masterEventId);
    setMasterEvent(me);
    const [members, activities, feedItems, competition] = await Promise.all([
      masterEventService.getMembers(masterEventId),
      masterEventService.getActivities(masterEventId),
      feedService.getForMasterEvent(masterEventId),
      competitionService.load(masterEventId),
    ]);
    setMembers(members);
    const visibleActivities = activities.map(a => a.type === 'betting' && typeof competition.bettingVisibility?.[a.id] === 'boolean'
      ? { ...a, obfuscated: competition.bettingVisibility[a.id] } : a);
    setActivities(visibleActivities);
    teamsService.register(masterEventId, activities);
    scoringService.register(masterEventId);
    setTeams(Array.isArray(competition.teams) ? competition.teams : []);
    setScores(Array.isArray(competition.scores) ? competition.scores : []);
    setFeed(feedItems);
    if (user) {
      setMyMembership(await masterEventService.getMembership(masterEventId, user.id));
    }
    setError('');
    } catch (err) { setError(err.message || 'Could not load event'); }
    finally { setLoading(false); }
  }, [masterEventId, user]);

  useEffect(() => {
    loadData();
    const timer = setInterval(loadData, 15000);
    window.addEventListener('focus', loadData);
    return () => { clearInterval(timer); window.removeEventListener('focus', loadData); };
  }, [loadData]);

  const role = myMembership?.role;
  const isOwner = role === 'owner'; // system-level owner
  const isEventAdmin = role === 'event-admin' || role === 'co-organizer' || isOwner;
  const isActivityAdmin = role === 'activity-admin' || isEventAdmin;
  // isAdmin = can manage the event (backward compat)
  const isAdmin = isEventAdmin;

  // Save to DynamoDB first, then mirror the confirmed update in local state.
  const updateActivityLocal = useCallback(async (activityId, updates) => {
    const saved = await masterEventService.updateActivity(activityId, updates);
    setActivities(prev => prev.map(a => a.id === activityId ? { ...a, ...updates, ...saved } : a));
    return saved;
  }, []);

  const value = {
    masterEvent,
    members,
    activities,
    teams,
    scores,
    feed,
    myMembership,
    isOwner,
    isEventAdmin,
    isActivityAdmin,
    isAdmin,
    loading,
    error,
    refreshData: loadData,
    updateActivityLocal,
  };

  return <MasterEventContext.Provider value={value}>{children}</MasterEventContext.Provider>;
}

export function useMasterEvent() {
  const context = useContext(MasterEventContext);
  if (!context) throw new Error('useMasterEvent must be used within MasterEventProvider');
  return context;
}

export function useOptionalMasterEvent() { return useContext(MasterEventContext); }
